import moment from 'moment';
import {
  isDefined,
  isStringDefined,
  isStringEmpty
} from '../../../utils/isDefined';
import { PROVIDERS_NUMBERS } from '../../../consts/initialValues';
import Optional from '../../../utils/optional';

const mapSubscriptionToOrderItem = ({
  data,
  template,
  client,
  mappedNotes
}) => {
  const {
    account,
    fullSubObject,
    name,
    shipping_address,
    att,
    order_confirmation_email,
    send_sim_card
  } = data;
  const extraSimCardToSendCount = [1, 2, 3].filter(
    extraCardNumber =>
      data[`extra_card_${extraCardNumber}_send_sim_card`] === true
  ).length;

  const subscriptionNote = `${
    send_sim_card ? 'Send simkort for primær, ' : ''
  }${
    extraSimCardToSendCount > 0
      ? `Send simkort for sekundær (${extraSimCardToSendCount}), `
      : ''
  }${mappedNotes}`;

  const foundedPrice =
    fullSubObject?.prices.find(price => price.sku === fullSubObject?.sku) || {};

  const product_characteristics = mapTemplateToCharacteristics({
    template,
    data
  });

  const orderItem = {
    action: 'add',
    identifier: 'SUBSCRIPTION',
    template_code: template?.code,
    account_reference: account.externalReference,
    client: client.name,
    notes: subscriptionNote,
    product_offering: {
      sku: fullSubObject?.sku,
      name: fullSubObject?.name
    },
    product_price: {
      currency: foundedPrice.currency || 'DKK',
      setup: {
        price: foundedPrice.setupPrice || '0'
      },
      recurring: {
        price: foundedPrice.price || '0',
        period: 'MONTH'
      }
    },
    product_characteristics,
    account: {
      id: account.id,
      name: account.name
    }
  };

  if (
    isDefined(shipping_address) &&
    (send_sim_card === true || extraSimCardToSendCount > 0)
  ) {
    const mapped_shipping_address = `${name}, ${shipping_address}${
      isStringDefined(att.trim()) ? `\n Att. ${att}` : ''
    } `;

    orderItem.product_offering.delivery_address = mapped_shipping_address;
  }

  if (isDefined(order_confirmation_email)) {
    orderItem.order_confirmation_email = order_confirmation_email;
  }

  return orderItem;
};

const mapNumberValue = (network, value) => {
  if (isStringEmpty(value)) {
    return '';
  }
  const networkPrefix = Optional(PROVIDERS_NUMBERS[network]).or(null);
  return isDefined(networkPrefix) ? `${networkPrefix}${value}` : value;
};

const mapTemplateToCharacteristics = ({ template, data }) => {
  let mappedTemplate = [];
  const { attributes } = template;

  const { mobile_number } = data;

  if (isDefined(mobile_number)) {
    const mobileNumberCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.resources-phone-numbers-primary'
    );

    if (isDefined(mobileNumberCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,

        {
          ...mobileNumberCharacteristic,
          value: mobile_number
        }
      ];
    }
  }

  const { first_name, last_name, network } = data;

  if (isDefined(first_name) || isDefined(last_name)) {
    const navn = attributes.find(
      attr => attr.name === 'user-info.user-info-navn'
    );
    if (isDefined(navn)) {
      mappedTemplate = [
        ...mappedTemplate,
        { ...navn, value: `${first_name} ${last_name}` }
      ];
    }
  }

  const { activate_esim } = data;

  if (isDefined(activate_esim) && activate_esim === true) {
    const activateEsimCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.esim-signup-primary'
    );
    if (isDefined(activateEsimCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,

        { ...activateEsimCharacteristic, value: activate_esim }
      ];
    }
  }

  const { mobile_sim_card_number } = data;

  if (isStringDefined(mobile_sim_card_number) && activate_esim !== true) {
    const simCardCharacteristic = attributes.find(
      attr =>
        attr.name === 'phone-and-sim-card.resources-sim-cards-primary' ||
        attr.name === 'mphone-and-sim-card.requested-sim-card'
    );
    if (isDefined(simCardCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,
        {
          ...simCardCharacteristic,
          value: mapNumberValue(network, mobile_sim_card_number)
        }
      ];
    }
  }

  const {
    activation_date,
    activationAsap,
    move_number_date,
    is_old_subscripion_date
  } = data;

  if (
    isDefined(activation_date) ||
    activationAsap === true ||
    isDefined(move_number_date) ||
    is_old_subscripion_date === true
  ) {
    const activationDateCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.activation-date'
    );
    if (isDefined(activationDateCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,
        {
          ...activationDateCharacteristic,
          value:
            isDefined(move_number_date) ||
            activationAsap === true ||
            is_old_subscripion_date === true
              ? moment().format('YYYY-MM-DD')
              : moment(activation_date).format('YYYY-MM-DD')
        }
      ];
    }
  }

  const { data_sharing_card } = data;

  if (isDefined(data_sharing_card) && data_sharing_card === true) {
    for (let cardNumber = 1; cardNumber <= 3; cardNumber += 1) {
      const isOptionalCard = cardNumber > 1;
      const isAdded = data[`extra_card_${cardNumber}_added`] === true;

      if (isOptionalCard && !isAdded) {
        continue;
      }

      const activateEsim = data[`extra_card_${cardNumber}_activate_esim`];
      const simCardNumber = data[`extra_card_${cardNumber}_sim_card_number`];
      const dataSharingEsimCharacteristic = attributes.find(
        attr =>
          attr.name === `phone-and-sim-card.esim-signup-extra-${cardNumber}`
      );
      if (isDefined(activateEsim) && isDefined(dataSharingEsimCharacteristic)) {
        mappedTemplate = [
          ...mappedTemplate,
          {
            ...dataSharingEsimCharacteristic,
            value: activateEsim
          }
        ];
      }

      if (isDefined(simCardNumber) && activateEsim !== true) {
        const dataSharingSimCardCharacteristic = attributes.find(
          attr =>
            attr.name ===
            `phone-and-sim-card.resources-sim-cards-extra-${cardNumber}`
        );
        if (isDefined(dataSharingSimCardCharacteristic)) {
          mappedTemplate = [
            ...mappedTemplate,
            {
              ...dataSharingSimCardCharacteristic,
              value: mapNumberValue(network, simCardNumber)
            }
          ];
        }
      }
    }
  }

  const { allow_recharge } = data;

  const refilllCharacteristic = attributes.find(
    attr =>
      attr.name === 'main-package.mp-refill' ||
      attr.name === 'mobile-refill.mobile-refill'
  );

  if (isDefined(refilllCharacteristic)) {
    mappedTemplate = [
      ...mappedTemplate,
      { ...refilllCharacteristic, value: allow_recharge }
    ];
  }

  const { selected } = data;

  if (isDefined(selected) && selected === 'port') {
    const portingCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.porting'
    );

    if (isDefined(portingCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,
        {
          ...portingCharacteristic,
          value: true
        }
      ];
    }

    const { move_number_date } = data;

    const portingDateCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.porting-port-request-date'
    );

    if (isDefined(portingDateCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,
        {
          ...portingDateCharacteristic,
          value: isDefined(move_number_date)
            ? moment(move_number_date).format('YYYY-MM-DD')
            : null
        }
      ];
    }

    const portingNuberCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.porting-msisdn'
    );

    if (isDefined(portingNuberCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,
        {
          ...portingNuberCharacteristic,
          value: data.mobile_number
        }
      ];
    }
    const { current_sim_card_number } = data;

    const portingSimCardCharacteristic = attributes.find(
      attr => attr.name === 'phone-and-sim-card.porting-donor-icc'
    );

    if (isDefined(portingSimCardCharacteristic)) {
      mappedTemplate = [
        ...mappedTemplate,
        {
          ...portingSimCardCharacteristic,
          value: current_sim_card_number
        }
      ];
    }
  }

  return mappedTemplate;
};

export default mapSubscriptionToOrderItem;
